import React from 'react'
import { AVAILABILITY_LABEL } from 'shared-constants'
import styled from 'styled-components'
import { Flex } from 'components/Layout'
import { Text } from 'components/Typography'
import { environment } from 'constants/environment'
import { useScrollTo } from 'hooks'
import { COLOR_INTENT, FONT_STACK, FONT_WEIGHT, SPACE } from 'Theme'
import { ProductAvailability } from 'types/graphql-generated'
import { formatLeadTimeString } from 'utils/formatters'

const StyledText = styled(Text)`
  cursor: pointer;
  width: fit-content;
`

const getProductAvailability = ({
  availableQuantity,
  minimumOrderQuantity,
  leadTime,
  enquire,
}: {
  availableQuantity: number
  minimumOrderQuantity: number
  leadTime?: number
  enquire?: boolean
}) => {
  if (leadTime) {
    return formatLeadTimeString(leadTime)
  }
  if (availableQuantity > 0 && availableQuantity >= minimumOrderQuantity) {
    return AVAILABILITY_LABEL.READY_TO_SHIP
  }
  return enquire === true ? null : AVAILABILITY_LABEL.OUT_OF_STOCK
}

interface VariantDescriptionProps {
  customDescription?: string
  availableQuantity: number
  minimumOrderQuantity: number
  isPriceUponRequest?: boolean
  location?: string
  detailsRef: React.RefObject<HTMLElement>
  leadTime?: number
  availability: ProductAvailability
  enquire?: boolean
  hasPublishedStatus: boolean
  hasCustomization?: boolean
}

const UnavailableProductAvailabilities = [
  ProductAvailability.Enquire,
  ProductAvailability.SoldOut,
] as const

export const VariantDescription: React.FC<
  React.PWC<VariantDescriptionProps>
> = ({
  customDescription,
  availableQuantity,
  minimumOrderQuantity,
  isPriceUponRequest,
  location,
  detailsRef,
  leadTime,
  availability,
  enquire,
  hasPublishedStatus,
  hasCustomization = false,
}) => {
  const { scrollToRef } = useScrollTo({ withHeaderOffset: true })

  const isProductUnavailable = UnavailableProductAvailabilities.some(
    (unavailableProductAvailability) =>
      availability === unavailableProductAvailability
  )

  const handleClickEnquire = () => {
    if (environment.CUSTOMER_SERVICE_FRAMEWORK === 'intercom') {
      window?.Intercom?.('show')
    } else {
      window?.FrontChat?.('show')
    }
  }

  return (
    <Flex flexDirection="column">
      {customDescription && <Text>{customDescription}</Text>}
      {!isPriceUponRequest && hasPublishedStatus && (
        <Text
          color={isProductUnavailable && COLOR_INTENT.PRODUCT_UNAVAILABLE_TEXT}
        >
          {getProductAvailability({
            availableQuantity,
            minimumOrderQuantity,
            leadTime,
            enquire,
          })}
          {location && ` in ${location}`}
        </Text>
      )}
      {hasCustomization && (
        <Text>
          Customization: Want a different fabric, finish, or size?{' '}
          <Text
            as="span"
            fontFamily={FONT_STACK.SANS}
            color={COLOR_INTENT.LINK.PRIMARY}
            onClick={handleClickEnquire}
            style={{ cursor: 'pointer' }}
          >
            Our team can help
          </Text>
        </Text>
      )}
      <StyledText
        onClick={() => {
          scrollToRef(detailsRef)
        }}
        fontWeight={FONT_WEIGHT.MEDIUM}
        color={COLOR_INTENT.BRAND}
        mt={SPACE.PX_20}
        mb={SPACE.PX_10}
      >
        Details and shipping
      </StyledText>
    </Flex>
  )
}
